import { Fragment } from "react";
import { isEmpty, pick, some, truncate, values } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot } from "@fortawesome/pro-regular-svg-icons";
import { Location } from "../../../@types/appointments";
import AppointmentLocationMap from "./AppointmentLocationMap.tsx";

// A URL has no place the browser may break at, so it breaks wherever the line
// happens to end, mid-word. A zero-width hint after every slash moves the
// break to where a reader expects it (tixxt/core#474).
export function withBreakHints(text: string) {
  const parts = text.split("/");

  return parts.map((part, index) => (
    <Fragment key={index}>
      {part}
      {index < parts.length - 1 && (
        <>
          {"/"}
          <wbr />
        </>
      )}
    </Fragment>
  ));
}

export function getLocationLinkText(location: Location, truncate65 = true) {
  switch (location.meeting_link_type) {
    case "ms_teams":
      return I18n.t("js.calendars.appointment.meeting.link_text.ms_teams");
    case "google_meet":
      return I18n.t("js.calendars.appointment.meeting.link_text.google_meet");
    case "zoom":
      return I18n.t("js.calendars.appointment.meeting.link_text.zoom");
    default:
      return truncate65 && location.link && location.link?.length > 65
        ? truncate(location.link, { length: 65 })
        : location.link;
  }
}

// the providers getLocationLinkText names instead of showing their URL
const MEETING_LINK_TYPES = ["ms_teams", "google_meet", "zoom"];

function isMeetingLink(location: Location) {
  return MEETING_LINK_TYPES.includes(location.meeting_link_type ?? "");
}

// A recognized provider keeps its name, any other link gets a generic label
function getLocationLinkName(location: Location) {
  return isMeetingLink(location)
    ? getLocationLinkText(location)
    : I18n.t("js.calendars.appointment.location.link_to_location");
}

export default function AppointmentLocation(
  location: Location & {
    showMap?: boolean;
    // The channel widget's card, which has few lines to spare
    // (tixxt/core#747): the location's name is clamped to two lines, with the
    // full one on hover, and links to the location instead of printing the
    // URL below it; a link without a name is labelled "Link zum Ort". A
    // meeting link stays below the name under its provider's name, which is
    // short and tells where the meeting takes place.
    compact?: boolean;
  },
) {
  const locationIsExtended = some(
    ["address1", "address2", "zip", "city", "link", "country"],
    (key) => !isEmpty(location[key]),
  );

  const addressForUri = encodeURIComponent(
    values(
      pick(location, ["address1", "address2", "zip", "city", "country"]),
    ).join(" "),
  );

  const linksName =
    location.compact && !!location.link && !isMeetingLink(location);

  return (
    <>
      {(!isEmpty(location.name) || locationIsExtended) && (
        <div className="detail-location detail-block flex gap-2 min-w-0">
          <FontAwesomeIcon className="detail-icon" icon={faLocationDot} />
          {/* A pasted URL has no space to break at, so the box has to be
              allowed to shrink below it (min-w-0) before `break-words` can do
              its work. Together they keep a long location inside the box, in
              the card and on a phone, and the break lands on a slash wherever
              the next segment still fits (tixxt/core#474). */}
          <address className="min-w-0 break-words">
            <>
              {location.name && (
                <div
                  className={`font-bold ${location.compact ? "line-clamp-2" : ""}`}
                  title={location.compact ? location.name : undefined}
                >
                  {linksName ? (
                    <a
                      href={location.link}
                      target="_blank"
                      rel="nofollow noreferrer"
                    >
                      {withBreakHints(location.name)}
                    </a>
                  ) : (
                    withBreakHints(location.name)
                  )}
                </div>
              )}
              {locationIsExtended && (
                <>
                  {location.address1 && <div>{location.address1}</div>}
                  {location.address2 && <div>{location.address2}</div>}
                  {(location.zip || location.city) && (
                    <div>{`${location.zip} ${location.city}`}</div>
                  )}
                  {location.country && <div>{location.country}</div>}
                  {/* a linked name already leads there */}
                  {location.link && !(linksName && location.name) && (
                    <div>
                      <a
                        href={location.link}
                        target="_blank"
                        rel="nofollow noreferrer"
                        // the label hides the URL, the hover shows it
                        title={location.compact ? location.link : undefined}
                      >
                        {location.compact
                          ? getLocationLinkName(location)
                          : withBreakHints(getLocationLinkText(location) || "")}
                      </a>
                    </div>
                  )}
                </>
              )}
            </>
          </address>
        </div>
      )}
      {location.maps_url && location.showMap && (
        <AppointmentLocationMap
          url={location.maps_url}
          address={addressForUri}
        />
      )}
    </>
  );
}
